import { useCallback, useMemo } from 'react';
import { noop } from '@babel/types';
import {
  documentId,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  QueryConstraint,
  startAfter,
  type QueryDocumentSnapshot,
} from '@firebase/firestore';
import type BaseEntity from 'benchmarking-domain/base-entity';
import type DataCollection from 'benchmarking-domain/data-collection';
import DataCollectionsHistoryEntry from 'benchmarking-domain/data-collections-history-entry';
import { DynamicQuestionnaire } from 'benchmarking-domain/dynamic-questionnaire';
import type Framework from 'benchmarking-domain/framework';
import { KpiBenchmarks } from 'benchmarking-domain/kpi-benchmark';
import { KpiStructure } from 'benchmarking-domain/kpi-definition';
import { KpisToSectionsAssignments } from 'benchmarking-domain/kpi-structure-dependency-finder';
import { KpiStatistics, ProjectKpiValues } from 'benchmarking-domain/kpi-value';
import { NormalizationFactors } from 'benchmarking-domain/normalization-factors';
import { NormalizationDetails } from 'benchmarking-domain/normalization/normalization-details';
import { NormalizedSectionData } from 'benchmarking-domain/normalized-section-data';
import type Organisation from 'benchmarking-domain/organisation';
import Plant from 'benchmarking-domain/plant';
import { PlantKpiHistory } from 'benchmarking-domain/plant-kpi-history';
import type Project from 'benchmarking-domain/project';
import ProjectProgressStatistics from 'benchmarking-domain/project-progress-statistics';
import ProjectSection from 'benchmarking-domain/project-section';
import type Questionnaire from 'benchmarking-domain/questionnaire';
import type User from 'benchmarking-domain/user';
import { firebaseApp } from 'firebase-app';
import {
  collection,
  connectFirestoreEmulator,
  doc,
  FieldPath,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  where,
  type DocumentData,
  type SnapshotOptions,
  type Unsubscribe,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { nameOf } from 'helpers/nameof';

import { useUser } from './use-user';

const database = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, 'europe-west1');

if (location.hostname === 'localhost') {
  connectFirestoreEmulator(database, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

const useApi = () => {
  const { user } = useUser();

  const userCollectionRef = useMemo(
    () => collection(database, 'users').withConverter<User>(genericEntityConverter),
    []
  );
  const organisationsCollectionRef = useMemo(
    () => collection(database, 'organisations').withConverter<Organisation>(genericEntityConverter),
    []
  );
  const frameworksCollectionRef = useMemo(
    () => collection(database, 'frameworks').withConverter<Framework>(genericEntityConverter),
    []
  );

  const subscribeProject = useCallback(
    (organisationId: string, projectId: string, onFetch: (result: Project | undefined) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId),
        'projects'
      ).withConverter<Project>(genericEntityConverter);
      const docRef = doc(collectionRef, projectId);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const subscribeProjects = useCallback(
    (organisationId: string, onFetch: (results: Project[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId),
        'projects'
      ).withConverter<Project>(genericEntityConverter);
      const queryConstraints: QueryConstraint[] = [];

      if (!user) {
        onFetch([]);
        return noop;
      } else if (!user.roles || user.roles.indexOf('superAdmin') === -1) {
        queryConstraints.push(where(new FieldPath('roles', user?.email ?? ''), '!=', null));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [organisationsCollectionRef, user]
  );

  const saveProject = useCallback(
    async (organisationId: string, project: Project) => {
      let docRef;
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId),
        'projects'
      ).withConverter<Project>(genericEntityConverter);

      if (!project.id || project.id === '') {
        docRef = doc(collectionRef);
      } else {
        docRef = doc(collectionRef, project.id);
      }

      await setDoc(docRef, project);

      return docRef.id;
    },
    [organisationsCollectionRef]
  );

  const archiveProject = useCallback(
    async (organisationId: string, projectId: string) => {
      const collectionRef = collection(doc(organisationsCollectionRef, organisationId), 'projectsArchive');
      const docRef = doc(collectionRef, projectId);

      await setDoc(docRef, { dummy: true }); //Save any object to trigger archiving
    },
    [organisationsCollectionRef]
  );

  const getProjectHistoryCount = useCallback(
    async (organisationId: string, projectId: string): Promise<number> => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId),
        'projects',
        projectId,
        'dataCollectionsHistory'
      ).withConverter<DataCollectionsHistoryEntry>(genericEntityConverter);

      const snapshot = await getCountFromServer(collectionRef);
      return snapshot.data().count;
    },
    [organisationsCollectionRef]
  );

  const fetchProjectHistoryPage = useCallback(
    async (
      organisationId: string,
      projectId: string,
      pageSize: number,
      lastItemCursor: any | undefined
    ): Promise<{ cursor: any; data: DataCollectionsHistoryEntry[] }> => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId),
        'projects',
        projectId,
        'dataCollectionsHistory'
      ).withConverter<DataCollectionsHistoryEntry>(genericEntityConverter);

      let firebaseQuery = query(
        collectionRef,
        orderBy(nameOf<DataCollectionsHistoryEntry>('updatedOn'), 'desc'),
        limit(pageSize)
      );

      if (lastItemCursor) {
        firebaseQuery = query(firebaseQuery, startAfter(lastItemCursor));
      }

      const snapshot = await getDocs(firebaseQuery);

      return {
        cursor: snapshot.docs[snapshot.docs.length - 1],
        data: snapshot.docs.map((d) => d.data()),
      };
    },
    [organisationsCollectionRef]
  );

  const subscribeQuestionnaires = useCallback(
    (frameworkId: string, publishedOnly: boolean, onFetch: (results: Questionnaire[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'questionnaires'
      ).withConverter<Questionnaire>(genericEntityConverter);
      const queryConstraints: QueryConstraint[] = [];

      if (publishedOnly) {
        queryConstraints.push(where('status', '==', 'published'));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeDynamicQuestionnaires = useCallback(
    (frameworkId: string, publishedOnly: boolean, onFetch: (results: DynamicQuestionnaire[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'dynamic-questionnaires'
      ).withConverter<DynamicQuestionnaire>(genericEntityConverter);
      const queryConstraints: QueryConstraint[] = [];

      if (publishedOnly) {
        queryConstraints.push(where('status', '==', 'published'));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeKpiStructure = useCallback(
    (frameworkId: string, onFetch: (results: KpiStructure[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'kpistructures'
      ).withConverter<KpiStructure>(genericEntityConverter);
      const q = query(collectionRef);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeCurrentKpiStructure = useCallback(
    (frameworkId: string, onFetch: (result: KpiStructure) => void): Unsubscribe => {
      const documentRef = doc(
        frameworksCollectionRef,
        frameworkId,
        'kpistructures',
        'current'
      ).withConverter<KpiStructure>(genericEntityConverter);

      return onSnapshot(documentRef, (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          const doc = documentSnapshot.data();
          onFetch(doc);
        }
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeCurrentKpiBenchmark = useCallback(
    (frameworkId: string, technology: string, onFetch: (result: KpiBenchmarks | undefined) => void): Unsubscribe => {
      const documentRef = doc(
        frameworksCollectionRef,
        frameworkId,
        'technology-kpi-benchmarks',
        technology,
        'kpi-benchmarks',
        'current'
      ).withConverter<KpiBenchmarks>(genericEntityConverter);

      return onSnapshot(documentRef, (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          const doc = documentSnapshot.data();
          onFetch(doc);
        } else {
          onFetch(undefined);
        }
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeKpiForSection = useCallback(
    (
      organisationId: string,
      projectId: string,
      sectionId: string,
      onFetch: (result: ProjectKpiValues | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'kpi-sections',
        sectionId
      ).withConverter<ProjectKpiValues>(genericPlainConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const saveQuestionnaire = useCallback(
    async (frameworkId: string, questionnaire: Questionnaire) => {
      let docRef;
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'questionnaires'
      ).withConverter<Questionnaire>(genericEntityConverter);

      if (!questionnaire.id || questionnaire.id === '') {
        docRef = doc(collectionRef);
      } else {
        docRef = doc(collectionRef, questionnaire.id);
      }

      await setDoc(docRef, questionnaire);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const saveDynamicQuestionnaire = useCallback(
    async (frameworkId: string, dynamicQuestionnaire: DynamicQuestionnaire) => {
      let docRef;
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'dynamic-questionnaires'
      ).withConverter<DynamicQuestionnaire>(genericEntityConverter);

      if (!dynamicQuestionnaire.id || dynamicQuestionnaire.id === '') {
        docRef = doc(collectionRef);
      } else {
        docRef = doc(collectionRef, dynamicQuestionnaire.id);
      }

      await setDoc(docRef, dynamicQuestionnaire);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const saveKpiStructure = useCallback(
    async (frameworkId: string, kpiStructure: KpiStructure) => {
      let docRef;
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'kpistructures'
      ).withConverter<KpiStructure>(genericEntityConverter);

      docRef = doc(collectionRef, 'current');

      await setDoc(docRef, kpiStructure);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const subscribeProjectProgressStatistics = useCallback(
    (
      organisationId: string,
      projectId: string,
      onFetch: (result: ProjectProgressStatistics | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'details',
        'projectProgressStatistics'
      ).withConverter<ProjectProgressStatistics>(genericPlainConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const subscribeProjectSections = useCallback(
    (organisationId: string, projectId: string, onFetch: (results: ProjectSection[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId, 'projects', projectId),
        'projectSections'
      ).withConverter<ProjectSection>(genericEntityConverter);
      const queryConstraints: QueryConstraint[] = [];

      if (!user) {
        onFetch([]);
        return noop;
      } else if (!user.roles || user.roles.indexOf('superAdmin') === -1) {
        queryConstraints.push(where(new FieldPath('roles', user?.email ?? ''), '!=', null));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [organisationsCollectionRef, user]
  );

  const subscribeProjectSection = useCallback(
    (
      organisationId: string,
      projectId: string,
      projectSectionId: string,
      onFetch: (result: ProjectSection | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'projectSections',
        projectSectionId
      ).withConverter<ProjectSection>(genericEntityConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const saveProjectSection = useCallback(
    async (organisationId: string, projectId: string, projectSection: ProjectSection) => {
      let docRef;
      const projectRef = collection(
        doc(organisationsCollectionRef, organisationId, 'projects', projectId),
        'projectSections'
      ).withConverter<DataCollection>(genericEntityConverter);

      if (!projectSection.id || projectSection.id === '') {
        docRef = doc(projectRef);
      } else {
        docRef = doc(projectRef, projectSection.id);
      }

      await setDoc(docRef, projectSection);

      return docRef.id;
    },
    [organisationsCollectionRef]
  );

  const subscribeDataCollections = useCallback(
    (organisationId: string, projectId: string, onFetch: (results: DataCollection[]) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(organisationsCollectionRef, organisationId, 'projects', projectId),
        'dataCollections'
      ).withConverter<DataCollection>(genericEntityConverter);
      const queryConstraints: QueryConstraint[] = [];

      if (!user) {
        onFetch([]);
        return noop;
      } else if (!user.roles || user.roles.indexOf('superAdmin') === -1) {
        queryConstraints.push(where(new FieldPath('roles', user?.email ?? ''), '!=', null));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [organisationsCollectionRef, user]
  );

  const subscribeDataCollection = useCallback(
    (
      organisationId: string,
      projectId: string,
      dataCollectionId: string,
      onFetch: (result: DataCollection | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'dataCollections',
        dataCollectionId
      ).withConverter<DataCollection>(genericEntityConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const saveDataCollection = useCallback(
    async (organisationId: string, projectId: string, dataCollection: DataCollection) => {
      let docRef;
      const projectRef = collection(
        doc(organisationsCollectionRef, organisationId, 'projects', projectId),
        'dataCollections'
      ).withConverter<DataCollection>(genericEntityConverter);

      if (!dataCollection.id || dataCollection.id === '') {
        docRef = doc(projectRef);
      } else {
        docRef = doc(projectRef, dataCollection.id);
      }

      await setDoc(docRef, dataCollection);

      return docRef.id;
    },
    [organisationsCollectionRef]
  );

  const subscribeProjectAllKpis = useCallback(
    (
      organisationId: string,
      projectId: string,
      onFetch: (result: ProjectKpiValues | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'all-kpis',
        'current'
      ).withConverter<ProjectKpiValues>(genericEntityConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const subscribeProjectSectionKpis = useCallback(
    (
      organisationId: string,
      projectId: string,
      dataCollectionId: string,
      onFetch: (result: ProjectKpiValues | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'kpi-sections',
        dataCollectionId
      ).withConverter<ProjectKpiValues>(genericEntityConverter);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const subscribeKpisToSectionsAssignments = useCallback(
    (frameworkId: string, onFetch: (result: KpisToSectionsAssignments) => void): Unsubscribe => {
      const documentRef = doc(
        frameworksCollectionRef,
        frameworkId,
        'kpistructures',
        'assignments-to-sections'
      ).withConverter<KpisToSectionsAssignments>(genericPlainConverter);

      return onSnapshot(documentRef, (documentSnapshot) => {
        if (documentSnapshot.exists()) {
          onFetch(documentSnapshot.data());
        }
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeUsers = useCallback(
    (organisationId: string | undefined, onFetch: (results: User[]) => void): Unsubscribe => {
      const queryConstraints: QueryConstraint[] = [];

      if (organisationId) {
        queryConstraints.push(where('organisationIds', 'array-contains', organisationId));
      }

      const q = query(userCollectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [userCollectionRef]
  );

  const subscribeUser = useCallback(
    (userId: string, onFetch: (result: User | undefined) => void): Unsubscribe => {
      const docRef = doc(userCollectionRef, userId);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [userCollectionRef]
  );

  const subscribeUserExists = useCallback(
    (email: string, onFetch: (result: boolean) => void): Unsubscribe => {
      const queryConstraints: QueryConstraint[] = [where('email', '==', email.toLowerCase())];

      const q = query(userCollectionRef, ...queryConstraints);

      return onSnapshot(q, (docSnapshot) => {
        onFetch(docSnapshot.size > 0);
      });
    },
    [userCollectionRef]
  );

  const saveUser = useCallback(
    async (user: User) => {
      let docRef;
      if (!user.id || user.id === '') {
        docRef = doc(userCollectionRef);
      } else {
        docRef = doc(userCollectionRef, user.id);
      }

      await setDoc(docRef, user);

      return docRef.id;
    },
    [userCollectionRef]
  );

  const subscribeOrganisations = useCallback(
    (onFetch: (results: Organisation[]) => void): Unsubscribe => {
      const queryConstraints: QueryConstraint[] = [];

      if (!user) {
        onFetch([]);
        return noop;
      } else if (!user.roles || user.roles.indexOf('superAdmin') === -1) {
        if (!user?.organisationIds || user?.organisationIds.length === 0) {
          onFetch([]);
          return () => {};
        }

        queryConstraints.push(where(documentId(), 'in', user?.organisationIds ?? []));
      }

      const q = query(organisationsCollectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [organisationsCollectionRef, user]
  );

  const subscribeOrganisation = useCallback(
    (organisationId: string, onFetch: (result: Organisation | undefined) => void): Unsubscribe => {
      const docRef = doc(organisationsCollectionRef, organisationId);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const saveOrganisation = useCallback(
    async (organisation: Organisation) => {
      let docRef;
      if (!organisation.id || organisation.id === '') {
        docRef = doc(organisationsCollectionRef);
      } else {
        docRef = doc(organisationsCollectionRef, organisation.id);
      }

      await setDoc(docRef, organisation);

      return docRef.id;
    },
    [organisationsCollectionRef]
  );

  const archiveOrganisation = useCallback(async (organisationId: string) => {
    const docRef = doc(database, 'organisationsArchive', organisationId);

    await setDoc(docRef, { dummy: true }); //Save any object to trigger archiving
  }, []);

  const subscribePlants = useCallback(
    (organisationId: string, onFetch: (results: Plant[]) => void): Unsubscribe => {
      const collectionRef = collection(doc(organisationsCollectionRef, organisationId), 'plants').withConverter<Plant>(
        genericEntityConverter
      );
      const queryConstraints: QueryConstraint[] = [];

      if (!user) {
        onFetch([]);
        return noop;
      } else if (!user.roles || user.roles.indexOf('superAdmin') === -1) {
        queryConstraints.push(where(new FieldPath('roles', user?.email ?? ''), '!=', null));
      }

      const q = query(collectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [organisationsCollectionRef, user]
  );

  const subscribePlant = useCallback(
    (organisationId: string, plantId: string, onFetch: (result: Plant | undefined) => void): Unsubscribe => {
      const collectionRef = collection(doc(organisationsCollectionRef, organisationId), 'plants').withConverter<Plant>(
        genericEntityConverter
      );
      const docRef = doc(collectionRef, plantId);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const savePlant = useCallback(
    async (organisationId: string, plant: Plant) => {
      let docRef;
      const collectionRef = collection(doc(organisationsCollectionRef, organisationId), 'plants').withConverter<Plant>(
        genericEntityConverter
      );

      if (!plant.id || plant.id === '') {
        docRef = doc(collectionRef);
      } else {
        docRef = doc(collectionRef, plant.id);
      }

      await setDoc(docRef, plant);

      return docRef.id;
    },
    [organisationsCollectionRef]
  );

  const archivePlant = useCallback(
    async (organisationId: string, plantId: string) => {
      const collectionRef = collection(doc(organisationsCollectionRef, organisationId), 'plantsArchive');
      const docRef = doc(collectionRef, plantId);

      await setDoc(docRef, { dummy: true }); //Save any object to trigger archiving
    },
    [organisationsCollectionRef]
  );

  const subscribeFrameworks = useCallback(
    (publishedOnly: boolean, onFetch: (results: Framework[]) => void): Unsubscribe => {
      const queryConstraints: QueryConstraint[] = [];

      if (publishedOnly) {
        queryConstraints.push(where('status', '==', 'published'));
      }

      const q = query(frameworksCollectionRef, ...queryConstraints);
      return onSnapshot(q, (querySnapshot) => {
        onFetch(querySnapshot.docs.map((d) => d.data()));
      });
    },
    [frameworksCollectionRef]
  );

  const saveFramework = useCallback(
    async (framework: Framework) => {
      let docRef;
      if (!framework.id || framework.id === '') {
        docRef = doc(frameworksCollectionRef);
      } else {
        docRef = doc(frameworksCollectionRef, framework.id);
      }

      await setDoc(docRef, framework);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const subscribeFramework = useCallback(
    (frameworkId: string, onFetch: (result: Framework | undefined) => void): Unsubscribe => {
      const docRef = doc(frameworksCollectionRef, frameworkId);
      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeNormalizationFactors = useCallback(
    (frameworkId: string, onFetch: (result: NormalizationFactors | undefined) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'normalizationFactors'
      ).withConverter<NormalizationFactors>(genericEntityConverter);
      const docRef = doc(collectionRef, 'current');

      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [frameworksCollectionRef]
  );

  const subscribeProductionNormalizationFactors = useCallback(
    (frameworkId: string, onFetch: (result: any | undefined) => void): Unsubscribe => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'normalizationFactors'
      ).withConverter<any>(genericEntityConverter);
      const docRef = doc(collectionRef, 'production');

      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [frameworksCollectionRef]
  );

  const saveNormalizationFactors = useCallback(
    async (frameworkId: string, normalizationFactors: NormalizationFactors) => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'normalizationFactors'
      ).withConverter<NormalizationFactors>(genericEntityConverter);

      const docRef = doc(collectionRef, 'current');
      await setDoc(docRef, normalizationFactors);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const saveProductionNormalizationFactors = useCallback(
    async (frameworkId: string, normalizationFactors: any) => {
      const collectionRef = collection(
        doc(frameworksCollectionRef, frameworkId),
        'normalizationFactors'
      ).withConverter<any>(genericEntityConverter);

      const docRef = doc(collectionRef, 'production');
      await setDoc(docRef, normalizationFactors);

      return docRef.id;
    },
    [frameworksCollectionRef]
  );

  const getKpiStatisticsForPlant = useCallback(
    async (
      organisationId: string,
      projectId: string,
      sectionId: string | undefined
    ): Promise<Map<string, KpiStatistics> | undefined> => {
      try {
        console.log('getKpiStatisticsForPlant');

        const getStatistics = httpsCallable(functions, 'getKpiStatisticsForPlant');
        const response = await getStatistics({ organisationId, projectId, sectionId });

        const kpiStatisticsMap = new Map<string, KpiStatistics>();

        if (Array.isArray(response?.data)) {
          const result = response.data as KpiStatistics[];
          result.forEach((kpiStat) => {
            kpiStatisticsMap.set(kpiStat.kpiId, kpiStat);
          });
        }

        return kpiStatisticsMap;
      } catch (error) {
        console.error('Error fetching KPI statistics:', error);
      }
    },
    []
  );

  const getNormalizationDetailsForProject = useCallback(
    async (
      frameworkId: string,
      organisationId: string,
      projectId: string
    ): Promise<NormalizationDetails | undefined> => {
      try {
        const getData = httpsCallable(functions, 'getNormalizationDetails');
        const response = await getData({ frameworkId, organisationId, projectId });
        return response.data as NormalizationDetails;
      } catch (error) {
        console.error('Error fetching normalization details:', error);
      }
    },
    []
  );

  const getPlantHistoryForKpis = useCallback(
    async (
      organisationId: string,
      projectId: string,
      sectionId: string | undefined
    ): Promise<PlantKpiHistory[] | undefined> => {
      try {
        const getData = httpsCallable(functions, 'getPlantHistoryForKpis');
        const response = await getData({ organisationId, projectId, sectionId });
        return response.data as PlantKpiHistory[];
      } catch (error) {
        console.error('Error fetching kpi history details:', error);
      }
    },
    []
  );

  const subscribeNormalizationData = useCallback(
    (
      organisationId: string,
      projectId: string,
      sectionId: string,
      onFetch: (result: NormalizedSectionData | undefined) => void
    ): Unsubscribe => {
      const docRef = doc(
        organisationsCollectionRef,
        organisationId,
        'projects',
        projectId,
        'normalizedSectionData',
        sectionId
      ).withConverter<NormalizedSectionData>(genericEntityConverter);

      return onSnapshot(docRef, (docSnapshot) => {
        onFetch(docSnapshot.data());
      });
    },
    [organisationsCollectionRef]
  );

  const createUserWithAuth = useCallback(async (user: User): Promise<string | undefined> => {
    try {
      const createUserWithAuth = httpsCallable(functions, 'createUserWithAuth');
      const response = await createUserWithAuth(user);

      return response.data as string | undefined;
    } catch (error) {
      console.error('Error creating user:', error);
      return undefined;
    }
  }, []);

  return {
    getKpiStatisticsForPlant,
    getNormalizationDetailsForProject,
    getPlantHistoryForKpis,
    subscribeNormalizationData,
    subscribeProject,
    subscribeProjects,
    saveProject,
    archiveProject,
    getProjectHistoryCount,
    fetchProjectHistoryPage,
    subscribeQuestionnaires,
    saveQuestionnaire,
    subscribeDynamicQuestionnaires,
    saveDynamicQuestionnaire,
    subscribeProjectProgressStatistics,
    subscribeKpiStructure,
    subscribeCurrentKpiStructure,
    subscribeCurrentKpiBenchmark,
    saveKpiStructure,
    subscribeKpiForSection,
    subscribeProjectSections,
    subscribeProjectSection,
    saveProjectSection,
    subscribeDataCollections,
    subscribeDataCollection,
    saveDataCollection,
    subscribeProjectAllKpis,
    subscribeProjectSectionKpis,
    subscribeKpisToSectionsAssignments,
    subscribeUserExists,
    subscribeUsers,
    subscribeUser,
    saveUser,
    createUserWithAuth,
    subscribeOrganisations,
    subscribeOrganisation,
    saveOrganisation,
    archiveOrganisation,
    subscribePlants,
    subscribePlant,
    savePlant,
    archivePlant,
    subscribeFrameworks,
    saveFramework,
    subscribeFramework,
    subscribeNormalizationFactors,
    subscribeProductionNormalizationFactors,
    saveNormalizationFactors,
    saveProductionNormalizationFactors,
  };
};

const genericEntityConverter = {
  toFirestore<T extends BaseEntity>(item: T): DocumentData {
    // eslint-disable-next-line no-unused-vars -- it is fine
    return (({ id, ...o }) => o)(item);
  },

  fromFirestore<T extends BaseEntity>(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions | undefined): T {
    const data = snapshot.data(options);

    return { ...data, id: snapshot.id } as T;
  },
};

const genericPlainConverter = {
  toFirestore<T extends DocumentData>(item: T): DocumentData {
    // eslint-disable-next-line no-unused-vars -- it is fine
    return item;
  },

  fromFirestore<T>(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions | undefined): T {
    const data = snapshot.data(options);

    return data as T;
  },
};

export default useApi;
